// ─────────────────────────────────────────────────────────────────────────────
// Modal — compatibility wrapper over the canonical BottomSheet primitive
//
// Historical: Modal.tsx shipped a centered-only desktop dialog with no focus
// trap, no sheet-stack z-coordination, no drag-to-dismiss, and no mobile-sheet
// variant. BottomSheet supplanted it for all new work.
//
// This module is now a thin wrapper that forwards every <Modal> / <ConfirmDialog>
// call site (~14 across Inbox, Settings, Workflows, Quotes, etc.) onto the
// canonical BottomSheet, picking up:
//   - mobile slide-up sheet with grab handle, desktop centered card
//   - focus trap (focus-trap-react), restores focus on close
//   - ARIA role="dialog" + aria-modal + labelled/described-by
//   - sheet-stack coordinated z-index (nested overlays stack correctly)
//   - refcounted body scroll lock
//   - ESC + backdrop close (toggleable via `dismissible`)
//   - keyboard avoidance for iOS soft keyboard
//
// Call sites keep their existing prop names — see audit at
// documents/files for AI/Study/modal-audit.md.
// ─────────────────────────────────────────────────────────────────────────────

import type { ReactNode } from 'react'
import { BottomSheet, type SheetSize } from './BottomSheet'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: ReactNode
  description?: ReactNode
  children: ReactNode
  footer?: ReactNode
  size?: SheetSize
  /** Allow close via backdrop tap / Esc / mobile drag. Default true. */
  dismissible?: boolean
  className?: string
  /** Accessible name when there's no visible title (UX-130). */
  ariaLabel?: string
}

export function Modal({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  dismissible = true,
  className,
  ariaLabel,
}: ModalProps) {
  // Legacy Modal callers pass `footer={<>cancel/confirm</>}` expecting a
  // right-aligned row. BottomSheet's footer slot stacks column-style by
  // default for its native consumers, so wrap in a row layout to preserve
  // visual parity with the historical Modal.
  const wrappedFooter = footer ? (
    <div className="flex flex-wrap items-center justify-end gap-3">
      {footer}
    </div>
  ) : undefined

  return (
    <BottomSheet
      open={isOpen}
      onClose={onClose}
      title={title}
      description={description}
      footer={wrappedFooter}
      size={size}
      dismissible={dismissible}
      className={className}
      ariaLabel={ariaLabel}
    >
      {children}
    </BottomSheet>
  )
}

// ConfirmDialog moved to ./ConfirmDialog (verb buttons, impact text, choices).
// Re-exported here so existing `import { ConfirmDialog } from './Modal'` works.
export { ConfirmDialog } from './ConfirmDialog'
